import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sparkle, Archive, Clock, X } from "@phosphor-icons/react";
import { toast } from "sonner";

// detail can be an object (409 duplicate_document, 402 subscription_required) —
// never hand it to toast.error raw or it renders "[object Object]".
const errMsg = (e, fallback) => {
  const detail = e?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object") return detail.message || detail.error || fallback;
  return fallback;
};

/**
 * UpsellCard — shown when a confirm attempt returns HTTP 402.
 *
 * Props:
 *   detail: the backend's 402 detail payload
 *     { tier, tier_name, price_monthly, description, doc_type,
 *       asset_id, ingest_id, store_only_available }
 *   householdId, ingestItem (the original item),
 *   confirmPayload (the original confirm POST body),
 *   onSubscribed   — called when subscribe succeeds + confirm retried
 *   onStored       — called when "Store only" path completes
 *   onPark         — called when "Park for later" picked
 *   onClose
 */
export default function UpsellCard({
  detail, householdId, ingestItem, confirmPayload,
  onSubscribed, onStored, onPark, onClose,
}) {
  const [busy, setBusy] = useState(null);

  const subscribe_and_retry = async () => {
    setBusy("subscribe");
    try {
      await api.post(
        `/subscriptions/subscribe?household_id=${householdId}`,
        { tier: detail.tier },
      );
      // Retry the confirm with the original payload
      const r = await api.post(
        `/ingest/${ingestItem.id}/confirm?household_id=${householdId}`,
        confirmPayload,
      );
      toast.success(`Subscribed to ${detail.tier_name} · $${detail.price_monthly}/mo`, {
        description: r.data?.destination || "Filed",
        duration: 7000,
      });
      onSubscribed?.(r.data);
    } catch (e) {
      toast.error(errMsg(e, "Subscription failed"));
    } finally {
      setBusy(null);
    }
  };

  const store_only = async () => {
    setBusy("store");
    try {
      // First, ensure storage_only tier is active (fall through 402 if not)
      const sub = await api.get(`/subscriptions?household_id=${householdId}`);
      if (!sub.data?.tiers?.storage_only?.active) {
        await api.post(
          `/subscriptions/subscribe?household_id=${householdId}`,
          { tier: "storage_only" },
        );
      }
      const r = await api.post(
        `/ingest/${ingestItem.id}/confirm?household_id=${householdId}`,
        { ...confirmPayload, store_only: true },
      );
      toast.success("Stored without parsing · $5/mo", {
        description: "Archived to /vault. No asset linkage.",
        duration: 7000,
      });
      onStored?.(r.data);
    } catch (e) {
      toast.error(errMsg(e, "Could not store"));
    } finally {
      setBusy(null);
    }
  };

  const park = () => { onPark?.(); };

  return (
    <div
      data-testid={`upsell-card-${ingestItem.id}`}
      className="mt-3 rounded-lg border-2 border-[hsl(18_53%_49%)] bg-gradient-to-br from-[hsl(18_53%_97%)] to-[hsl(36_45%_96%)] p-5 relative"
    >
      <button
        data-testid={`upsell-close-${ingestItem.id}`}
        onClick={onClose}
        className="absolute top-3 right-3 text-muted-foreground hover:text-foreground"
        aria-label="Dismiss upsell"
      >
        <X size={16} />
      </button>
      <div className="flex items-start gap-3 mb-3">
        <div className="rounded-full bg-[hsl(18_53%_49%)] p-2 shrink-0">
          <Sparkle size={18} weight="fill" className="text-white" />
        </div>
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.14em] font-semibold text-[hsl(18_53%_49%)]">
            Subscribe to unlock
          </p>
          <h3 className="font-serif text-xl leading-tight mt-0.5">
            {detail.tier_name} · ${detail.price_monthly}/mo
          </h3>
          <p className="text-sm text-muted-foreground mt-1">{detail.description}</p>
        </div>
      </div>

      <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
        This {detail.doc_type?.replace(/_/g, " ") || "document"} can&apos;t be tracked under
        your current plan. Pick how to handle it:
      </p>

      <div className="grid sm:grid-cols-3 gap-2">
        <Button
          data-testid={`upsell-track-${ingestItem.id}`}
          onClick={subscribe_and_retry}
          disabled={busy !== null}
          className="rounded-sm font-semibold bg-[hsl(18_53%_49%)] hover:bg-[hsl(18_53%_42%)] text-white h-auto py-2.5 px-3 flex-col items-start"
        >
          <span className="text-[11px] uppercase tracking-wider opacity-80">Recommended</span>
          <span className="text-sm">Store & Track · ${detail.price_monthly}/mo</span>
        </Button>
        {detail.store_only_available && (
          <Button
            data-testid={`upsell-store-${ingestItem.id}`}
            onClick={store_only}
            disabled={busy !== null}
            variant="outline"
            className="rounded-sm h-auto py-2.5 px-3 flex-col items-start"
          >
            <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
              <Archive size={11} className="inline" /> Archive only
            </span>
            <span className="text-sm">Store only · $5/mo</span>
          </Button>
        )}
        <Button
          data-testid={`upsell-park-${ingestItem.id}`}
          onClick={park}
          disabled={busy !== null}
          variant="ghost"
          className="rounded-sm h-auto py-2.5 px-3 flex-col items-start"
        >
          <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
            <Clock size={11} className="inline" /> Decide later
          </span>
          <span className="text-sm">Park for now</span>
        </Button>
      </div>

      {busy && (
        <p data-testid={`upsell-busy-${ingestItem.id}`} className="text-xs text-muted-foreground mt-3">
          {busy === "subscribe" ? "Subscribing and filing…" : "Storing in Archive…"}
        </p>
      )}
    </div>
  );
}
